import React, { ChangeEvent, useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import { SelectChangeEvent } from '@mui/material';

import { CustomAlert, CustomButton, CustomInput, CustomSelect } from '../../../../../../../shared/uiComponents';
import { errorNormalizer, validateNumber } from '../../../../../../../shared/Helpers/functions';
import { initialState } from './initialState';
import { ACTIONS, TargetReducer } from './reducer';

import { DispatchProperties, useSelector } from '../../../../../../../redux/store';
import { getMasteryCriteria, getTargets, updateTarget } from '../../../../../../../redux/State/clientSlice/programTargetSlice';
import { TargetProperties } from '../../../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';
import { EditSvg } from '../../../../../../../shared/Assets/svgIcons';

interface EditProps {
	item: TargetProperties;
	mobileButton?: boolean;
	programId: string;
}

export const UpdateTargetContent = ({
	closeHandler,
	targetProperties,
	id,
	programId,
}: {
	closeHandler: () => void;
	id: string;
	programId: string;
	targetProperties: TargetProperties;
}) => {
	const dispatch = useDispatch<DispatchProperties>();

	const loading = useSelector((state) => state.programTarget.loading);
	const masteryOptions = useSelector((state) => state.programTarget.masteryCriterias);
	const includeMastered = useSelector((state) => state.programTarget.includeMastered);

	const [targetData, setTargetData] = useReducer(TargetReducer, initialState);
	const {
		name,
		// goalName,
		errorCorrection,
		reinforcement,
		sd,
		targetInstructions,
		masteryCriteria,
		consecutiveDays,
		responsePercentage,
	} = targetData;

	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!targetProperties) return;
		setTargetData({ type: ACTIONS.setState, payload: targetProperties });
		setTargetData({
			type: ACTIONS.setMasteryCriteria,
			payload: targetProperties.masteryCriteria.id,
		});

		if (targetProperties.consecutiveDays === 0) {
			setTargetData({ type: ACTIONS.setConsecutiveDays, payload: null });
		}
	}, [targetProperties]);

	useEffect(() => {
		if (name) {
			setError('');

			return;
		}

		setError('Required');
	}, [name]);

	useEffect(() => {
		if (masteryOptions.length) return;
		dispatch(getMasteryCriteria());
	}, [dispatch, masteryOptions]);

	const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setName, payload: value });
	};

	// const onGoalNameChange = (event: ChangeEvent<HTMLInputElement>) => {
	//   const { value } = event.target;
	//   setTargetData({ type: ACTIONS.setGoalName, payload: value });
	// };

	const onSdChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setSD, payload: value });
	};

	const onTargetInstructionsChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setTargetInstructions, payload: value });
	};

	const onErrorCorrectionChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setErrorCorrection, payload: value });
	};

	const onReinforcementChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setReinforcement, payload: value });
	};

	const updateTagetHandler = () => {
		if (!programId || !id) return;
		dispatch(updateTarget({ data: targetData, programId, id }))
			.then(unwrapResult)
			.then(() => {
				dispatch(getTargets({ programId, includeMastered }));
				closeHandler();
				toast('Updated');
			})
			.catch(errorNormalizer);
	};

	const onMasteryCriteriaChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		const payload = parseInt(value);
		setTargetData({ type: ACTIONS.setMasteryCriteria, payload });
	};

	const onConsecutiveDaysChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setTargetData({
			type: ACTIONS.setConsecutiveDays,
			payload: value,
		});
	};

	const onResponsePercentageChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setTargetData({
			type: ACTIONS.setResponsePercentage,
			payload: value,
		});
	};

	return (
		<div>
			<CustomInput
				label="Target Name"
				className="marginBottom16"
				value={name}
				onChange={onNameChange}
				error={!name}
				errorMessage={error}
				backgroundType="base"
			/>
			<CustomInput
				label="SD"
				placeholder="Required"
				className="marginBottom16"
				value={sd}
				onChange={onSdChange}
				backgroundType="base"
			/>
			<CustomSelect
				label="Mastery Criteria"
				className="marginBottom16"
				value={masteryCriteria.toString()}
				setValue={onMasteryCriteriaChange}
				data={masteryOptions}
			/>
			<CustomInput
				label="Consecutive Days"
				className="marginBottom16"
				value={consecutiveDays}
				onChange={onConsecutiveDaysChange}
				error={!consecutiveDays}
				backgroundType="base"
			/>
			<CustomInput
				label="Response Percentage"
				className="marginBottom16"
				value={responsePercentage}
				onChange={onResponsePercentageChange}
				error={!responsePercentage}
				backgroundType="base"
			/>
			{/* <CustomInput
        label="Goal Name (for reporting)"
        className="marginBottom16"
        value={goalName}
        onChange={onGoalNameChange}
        multiline={true}
        backgroundType="base"
      /> */}
			<CustomInput
				label="Target Instructions"
				placeholder="Required"
				className="marginBottom16"
				value={targetInstructions}
				onChange={onTargetInstructionsChange}
				multiline
				backgroundType="base"
			/>
			<CustomInput
				placeholder="Required"
				label="Error Correction"
				className="marginBottom16"
				value={errorCorrection}
				onChange={onErrorCorrectionChange}
				multiline
				backgroundType="base"
			/>
			<CustomInput
				placeholder="Required"
				label="Reinforcement"
				className="marginBottom16"
				value={reinforcement}
				onChange={onReinforcementChange}
				multiline
				backgroundType="base"
			/>
			<CustomButton
				title="Save"
				onClick={updateTagetHandler}
				loading={loading}
				disabled={loading || !name || !consecutiveDays || !responsePercentage}
			/>
		</div>
	);
};

const Edit = ({ item, programId, mobileButton }: EditProps) => {
	const [openEdit, setOpenEdit] = useState<boolean>(false);
	const { id } = item;

	const handleOpenEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setOpenEdit(true);
	};

	return (
		<>
			{mobileButton ? (
				<div
					className="targetActionButton"
					onClick={(e) => {
						e.stopPropagation();
						setOpenEdit(true);
					}}
				>
					<EditSvg color="#62615F" />
				</div>
			) : (
				<CustomButton
					iconOnly={<EditSvg color="#FFFFFF" />}
					onClick={handleOpenEdit}
					buttonType="primary"
					sizeType="md"
				/>
			)}
			<CustomAlert
				title="Target Information"
				open={openEdit}
				onClose={() => setOpenEdit(false)}
				Content={() => (
					<UpdateTargetContent
						closeHandler={() => setOpenEdit(false)}
						id={id}
						programId={programId}
						targetProperties={item}
					/>
				)}
			/>
		</>
	);
};

export default Edit;
